import React, { useEffect } from 'react';
import {
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Container,
	Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { paths } from '@/app/routes';

/**
 * Page that Microsoft redirects to after admin consent flow, with query parameters indicating the result of the consent process. 
 * This page will call our backend to store the tenant ID and consent result, then redirect user back to home page.
 */
const AdminConsentProcessPage = () => {
	const consentResult = getConsentResultFromQueryParameters(useLocation());

	useEffect(() => {
		const callbackBaseUrl = process.env.REACT_APP_TENANT_CONSENT_ENDPOINT;
		if (!callbackBaseUrl) {
			console.error('Missing REACT_APP_TENANT_CONSENT_ENDPOINT');
			return;
		}

		fetch(`${callbackBaseUrl}/callback`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(consentResult),
		}).catch((error) => {
			console.error('Failed to send tenant consent callback', error);
		});
	}, [consentResult]);

	return (
		<Container maxWidth='sm' sx={{ mt: 10, textAlign: 'center' }}>
			<Card>
				<CardHeader
					title={consentResult.isConsentGranted ? 'Admin Consent Received' : 'Admin Consent Not Completed'}
				/>
				<CardContent>
					<Typography>
						{consentResult.isConsentGranted
							? 'Microsoft tenant consent has been processed. Sign in to Folio to finish linking this tenant.'
							: 'We could not confirm admin consent from Microsoft. Please retry the consent process from Folio.'}
					</Typography>
					{consentResult.tenantId && (
						<Typography sx={{ mt: 2 }} color='text.secondary'>
							Tenant ID: {consentResult.tenantId}
						</Typography>
					)}
				</CardContent>
				<CardActions sx={{ justifyContent: 'center', p: 2 }}>
					<Button color='primary' variant='contained' href={paths.home}>
						Continue to Folio
					</Button>
				</CardActions>
			</Card>
		</Container>
	);
};

/**
 * Error code for the known Microsoft bug where tenant ID is not returned in the 'tenant' query param but instead an error is thrown with the tenant ID embedded in the error description.
 * See: https://learn.microsoft.com/en-us/answers/questions/2337285/azure-app-registration-0auth-authorize-error-aadst
 */
const EXISTING_SERVICE_PRINCIPAL_ERROR_CODE = 'AADSTS650051';

/**
 * Query parameters we expect from Microsoft after admin consent flow
 */
const QUERY_PARAMS = {
	TENANT: 'tenant',
	CONSENT: 'admin_consent',
	ERROR: 'error',
	ERROR_DESCRIPTION: 'error_description',
	STATE: 'state',
}

/**
 * Gets tenant ID from search params, handling both normal and AADSTS650051 error cases
 * In the normal case, tenant id is in the 'tenant' search param. 
 * However there is a bug on Microsoft's side where sometimes a new tenant will redirect with an error AADSTS650051 and the tenant ID is embedded in the 'error_description' param instead. 
 * In that case, tenant id is embedded in the 'error_description' param
 * 
 * See: https://learn.microsoft.com/en-us/answers/questions/2337285/azure-app-registration-0auth-authorize-error-aadst
 */
const getTenantId = (searchParams) => {
	const tenantId = searchParams.get(QUERY_PARAMS.TENANT);
	if (tenantId) return tenantId;

	// Handle AADSTS650051 error case where tenant ID is in error_description
	// Ugly as sin, but we need to parse the tenant ID out of the error description string. The format of the string looks like this:
	// "AADSTS650051: Consent action for ... the tenant 37de2533-9046-46af-8555-edc0fadd1c36 paramName: ServicePrincipalName..."
	const errorDescription = searchParams.get(QUERY_PARAMS.ERROR_DESCRIPTION);
	if (errorDescription?.includes(EXISTING_SERVICE_PRINCIPAL_ERROR_CODE)) {
		const tenantMatch = errorDescription.match(
			/for\s+the\s+tenant\s+([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i
		);

		const tenantId = tenantMatch?.[1];
		return tenantId;
	}

	return null;
}

const isExistingServicePrincipalError = (searchParams) => {
	const errorDescription = searchParams.get(QUERY_PARAMS.ERROR_DESCRIPTION);
	return errorDescription?.includes(EXISTING_SERVICE_PRINCIPAL_ERROR_CODE);
};

/**
 * If microsoft returns an error due to the existing service principal bug, we want to ignore the error and treat it as a successful consent
 * So in that case we return null for error description.
 * In other cases, we return the error description from the search params as normal.
 */
const getErrorDescription = (searchParams) => {
	if (isExistingServicePrincipalError(searchParams)) {
		return null;
	}

	return searchParams.get(QUERY_PARAMS.ERROR_DESCRIPTION);
};


/**
 * If microsoft returns an error due to the existing service principal bug, we want to ignore the error and treat it as a successful consent
 * So in that case we return null for error.
 * In other cases, we return the error from the search params as normal.
 */
const getError = (searchParams) => {
	if (isExistingServicePrincipalError(searchParams)) {
		return null;
	}

	return searchParams.get(QUERY_PARAMS.ERROR);
};

/**
 * Pulls out the relevant consent result information from the query parameters in the URL, handling both normal and error cases
 * @param {*} location 
 * @returns {{
 *   tenantId: string | null,
 *   isConsentGranted: boolean,
*  	 state: string | null,
 *   error: string | null,
 *   errorDescription: string | null
 * }}
 */
const getConsentResultFromQueryParameters = (location) => {
	const searchParams = new URLSearchParams(location.search);
	const tenantId = getTenantId(searchParams);
	const error = getError(searchParams);
	const errorDescription = getErrorDescription(searchParams);
	const state = searchParams.get(QUERY_PARAMS.STATE);
	const consent = searchParams.get(QUERY_PARAMS.CONSENT);

	// Microsoft redirects with consent=True even if admin consent was not actually granted
	// So we have to check that there is a tenant ID as well to confirm consent was granted
	const isConsentGranted = consent === 'True' && Boolean(tenantId);

	return { tenantId, isConsentGranted, error, errorDescription, state };
}

export default AdminConsentProcessPage;
